/**
 * Splitting bookings across the bills of one modal (doubles: one booking, each team pays its part). Only a preview
 * and the inputs' arithmetic: the server (billSplit.ts) checks that each booking's parts add up to its amount.
 *
 * `Parts` holds, per booking id, its amount on each bill of the modal (same order as the bills). Whole rupees.
 */
export type Parts = Record<string, number[]>;

/**
 * `total` shared out in proportion to `weights`, in whole rupees, adding up to `total` exactly (largest remainder;
 * a tie goes to the earlier one). All weights 0 → equal parts. Same as the server's `spread`.
 */
export function spread(total: number, weights: number[]): number[] {
  if (!weights.length) return [];
  const sum = weights.reduce((a, w) => a + w, 0);
  const exact = weights.map((w) => (sum > 0 ? (total * w) / sum : total / weights.length));
  const out = exact.map(Math.floor);
  let left = total - out.reduce((a, n) => a + n, 0);
  const order = exact.map((x, i) => ({ i, rest: x - Math.floor(x) })).sort((a, b) => b.rest - a.rest || a.i - b.i);
  for (let k = 0; left > 0 && order.length; k = (k + 1) % order.length, left--) out[order[k]!.i]! += 1;
  return out;
}

interface Amounted {
  id: string;
  amount: number | null;
}

/** Every booking whole on the first bill (no split yet). */
export function allOnFirst(bookings: Amounted[], bills: number): Parts {
  return Object.fromEntries(bookings.map((b) => [b.id, Array.from({ length: bills }, (_, i) => (i === 0 ? b.amount ?? 0 : 0))]));
}

/** Every booking shared equally between the bills. */
export function equalParts(bookings: Amounted[], bills: number): Parts {
  return Object.fromEntries(bookings.map((b) => [b.id, spread(b.amount ?? 0, Array(bills).fill(1))]));
}

/**
 * Bill `bill` takes `value` of a booking (clamped to 0…amount); what is left goes to the other bills in proportion
 * to what they had (equally when they had nothing).
 */
export function setPart(parts: number[], amount: number, bill: number, value: number): number[] {
  const v = Math.max(0, Math.min(amount, Math.round(value) || 0));
  const others = parts.map((p, i) => (i === bill ? null : p));
  const rest = spread(amount - v, others.filter((p): p is number => p != null));
  let k = 0;
  return parts.map((_, i) => (i === bill ? v : rest[k++]!));
}

/**
 * Bill `bill` pays `total` of the table time: shared over the bookings in proportion to their amounts, the rest of
 * each booking to the other bills (see setPart). Staff can then adjust any booking.
 */
export function setBillTotal(bookings: Amounted[], parts: Parts, bill: number, total: number): Parts {
  const all = bookings.reduce((a, b) => a + (b.amount ?? 0), 0);
  const t = Math.max(0, Math.min(all, Math.round(total) || 0));
  const mine = spread(t, bookings.map((b) => b.amount ?? 0));
  return Object.fromEntries(bookings.map((b, i) => [b.id, setPart(parts[b.id]!, b.amount ?? 0, bill, mine[i]!)]));
}

/** A bill added: it starts with nothing (the other bills keep their parts). */
export function addBill(parts: Parts): Parts {
  return Object.fromEntries(Object.entries(parts).map(([id, p]) => [id, [...p, 0]]));
}

/** A bill removed: its parts go back to the first bill (or the next one when the first is removed). */
export function removeBill(parts: Parts, bill: number): Parts {
  const to = bill === 0 ? 1 : 0;
  return Object.fromEntries(
    Object.entries(parts).map(([id, p]) => {
      const next = p.map((x, i) => (i === to ? x + p[bill]! : x));
      next.splice(bill, 1);
      return [id, next];
    }),
  );
}

/** Parts kept from a draft, when they still fit the bookings and the bills; else every booking on the first bill. */
export function reconcile(saved: Parts | undefined, bookings: Amounted[], bills: number): Parts {
  const fresh = allOnFirst(bookings, bills);
  if (!saved) return fresh;
  for (const b of bookings) {
    const p = saved[b.id];
    if (p && p.length === bills && p.reduce((a, x) => a + x, 0) === (b.amount ?? 0)) fresh[b.id] = p;
  }
  return fresh;
}
